import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import RoleAdaptiveNavbar from '../../components/ui/RoleAdaptiveNavbar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [galleryCode, setGalleryCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [accessedGalleries, setAccessedGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Load accessed galleries
  useEffect(() => {
    const loadAccessedGalleries = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          ?.from('user_gallery_access')
          ?.select(`
            id,
            first_accessed_at,
            last_accessed_at,
            access_count,
            galleries (
              id,
              gallery_name,
              description,
              gallery_code,
              gallery_type,
              created_at,
              user_profiles!galleries_creator_id_fkey (
                full_name
              )
            )
          `)
          ?.eq('user_id', user?.id)
          ?.order('last_accessed_at', { ascending: false });

        if (error) throw error;

        // Get media count for each gallery
        const galleriesWithMedia = await Promise.all(
          data?.map(async (access) => {
            const { count } = await supabase
              ?.from('media')
              ?.select('*', { count: 'exact', head: true })
              ?.eq('gallery_id', access?.galleries?.id);

            return {
              ...access,
              galleries: {
                ...access?.galleries,
                mediaCount: count || 0
              }
            };
          }) || []
        );

        setAccessedGalleries(galleriesWithMedia);
      } catch (error) {
        console.error('Error loading galleries:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAccessedGalleries();
  }, [user]);

  const handleEnterGalleryCode = async (e) => {
    e?.preventDefault();
    
    if (!galleryCode?.trim()) {
      setCodeError('Please enter a gallery code');
      return;
    }

    setIsSubmittingCode(true);
    setCodeError('');

    try {
      // Find gallery by code
      const { data: galleries, error } = await supabase
        ?.rpc('get_gallery_by_code', { p_gallery_code: galleryCode?.trim()?.toUpperCase() });

      if (error) throw error;

      if (!galleries || galleries?.length === 0) {
        setCodeError('Invalid gallery code. Please check and try again.');
        setIsSubmittingCode(false);
        return;
      }

      const gallery = galleries?.[0];

      // Record gallery access
      await supabase?.rpc('record_gallery_access', { p_gallery_id: gallery?.id });

      // Navigate to gallery viewer
      navigate(`/gallery-viewer?id=${gallery?.id}`);
    } catch (error) {
      console.error('Error entering gallery:', error);
      setCodeError('An error occurred. Please try again.');
    } finally {
      setIsSubmittingCode(false);
    }
  };

  const handleGalleryClick = async (galleryId) => {
    try {
      // Update access record
      await supabase?.rpc('record_gallery_access', { p_gallery_id: galleryId });
      
      // Navigate to gallery
      navigate(`/gallery-viewer?id=${galleryId}`);
    } catch (error) {
      console.error('Error accessing gallery:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const filteredGalleries = accessedGalleries?.filter(access => 
    access?.galleries?.gallery_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    access?.galleries?.user_profiles?.full_name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>My Galleries - RVS Gallery Platform</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <RoleAdaptiveNavbar />

        <main className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">My Galleries</h1>
            <p className="text-muted-foreground">
              Access your gallery collection and discover new galleries with codes
            </p>
          </div>

          {/* Gallery Code Entry Section */}
          <div className="bg-card border border-border rounded-xl p-6 mb-8 shadow-warm-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="Key" size={24} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-1">Enter Gallery Code</h2>
                <p className="text-sm text-muted-foreground">
                  Have a gallery code? Enter it below to access a new gallery
                </p>
              </div>
            </div>

            <form onSubmit={handleEnterGalleryCode} className="flex gap-3">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Enter 8-character gallery code (e.g., ABC12345)"
                  value={galleryCode}
                  onChange={(e) => {
                    setGalleryCode(e?.target?.value?.toUpperCase());
                    setCodeError('');
                  }}
                  error={codeError}
                  maxLength={8}
                />
              </div>
              <Button
                type="submit"
                variant="default"
                loading={isSubmittingCode}
                iconName="ArrowRight"
                iconPosition="right"
                className="flex-shrink-0"
              >
                Access Gallery
              </Button>
            </form>
          </div>

          {/* Search Bar */}
          {accessedGalleries?.length > 0 && (
            <div className="mb-6">
              <Input
                type="text"
                placeholder="Search galleries by name or contributor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                iconName="Search"
              />
            </div>
          )}

          {/* My Galleries Section */}
          <div className="mb-6">
            <h2 className="text-2xl font-semibold mb-4">My Accessed Galleries</h2>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={32} className="animate-spin" color="var(--color-primary)" />
              </div>
            ) : filteredGalleries?.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                  <Icon name="FolderOpen" size={32} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {searchQuery ? 'No galleries found' : 'No galleries yet'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {searchQuery 
                    ? 'Try adjusting your search terms' :'Enter a gallery code above to access your first gallery'
                  }
                </p>
                {searchQuery && (
                  <Button
                    variant="outline"
                    onClick={() => setSearchQuery('')}
                    iconName="X"
                    iconPosition="left"
                  >
                    Clear Search
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredGalleries?.map((access) => (
                  <div
                    key={access?.id}
                    className="bg-card border border-border rounded-xl overflow-hidden smooth-transition hover-lift shadow-warm-sm cursor-pointer"
                    onClick={() => handleGalleryClick(access?.galleries?.id)}
                  >
                    <div className="relative h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <Icon name="Image" size={48} color="var(--color-muted-foreground)" />
                      <div className="absolute top-3 right-3">
                        <span className="bg-card/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-medium border border-border">
                          {access?.galleries?.gallery_code}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <h3 className="text-lg font-semibold mb-1 line-clamp-1">
                        {access?.galleries?.gallery_name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {access?.galleries?.description || 'No description available'}
                      </p>

                      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                        <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                        <span className="text-sm text-muted-foreground">
                          by {access?.galleries?.user_profiles?.full_name}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Media Files</p>
                          <p className="text-sm font-medium data-text">
                            {access?.galleries?.mediaCount || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Visits</p>
                          <p className="text-sm font-medium data-text">
                            {access?.access_count}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          <span>First: {formatDate(access?.first_accessed_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          <span>Last: {formatDate(access?.last_accessed_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default UserDashboard;